import * as loglevel from 'loglevel'
import { UAParser } from 'ua-parser-js'
import loglevelPluginServer from './loglevelPluginServer'
import {
  AccessToken,
  defaultAppName,
  IdToken,
  LogFormattedData,
  LoglevelPluginServerOptions,
  LogMessage,
  LogMethods,
  LogMissingParamPlaceholders,
  LogRawData
} from './types'
import { getPartnerName } from './utils'

const logger = loglevel.getLogger('common-utils')

const parser = new UAParser()
const { device, os, browser } = parser.getResult()

if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test') {
  const options: LoglevelPluginServerOptions = {
    url: `${process.env.LOGJAM_URL}log`,
    reqHeaders: {
      'Content-Type': 'application/json',
      'X-Origin': defaultAppName
    }
  }

  loglevelPluginServer(logger, options)
}

if (
  process.env.NODE_ENV === 'production' &&
  process.env.ENVIRONMENT === 'prod'
) {
  logger.setDefaultLevel(loglevel.levels.DEBUG)
} else {
  logger.enableAll()
}

const makeMessage = (
  message: string,
  window: Window,
  access_token?: AccessToken,
  id_token?: IdToken,
  otherParams?: object
): LogMessage => {
  const now = Date.now()

  return {
    ...otherParams,
    partnerName: getPartnerName(),
    applicationSource: access_token
      ? access_token.ApplicationSource ||
        LogMissingParamPlaceholders.ApplicationSource
      : LogMissingParamPlaceholders.AccessToken,
    crmContactId: access_token
      ? access_token.CrmContactId || LogMissingParamPlaceholders.CrmContactId
      : LogMissingParamPlaceholders.AccessToken,
    nonce: id_token
      ? id_token.nonce || LogMissingParamPlaceholders.Nonce
      : LogMissingParamPlaceholders.IdToken,
    timestamp: now,
    localTimeString: new Date(now).toString(),
    message,
    threatMetrixToken:
      window.threatMetrixToken || LogMissingParamPlaceholders.tmxToken,
    crmAccountGuid:
      window.moneycorpCrmAccountGuid ||
      LogMissingParamPlaceholders.CrmAccountGuid,
    navigatorUserAgent: window.navigator.userAgent,
    navigatorAppVersion: window.navigator.appVersion,
    navigatorAppName: window.navigator.appName,
    navigatorCookiesEnabled: window.navigator.cookieEnabled,
    navigatorLocale: window.navigator.language,
    navigatorScreenSize: `${window.screen.width}x${window.screen.height}`,
    bundleVersion: window.version,
    browserName: browser.name || LogMissingParamPlaceholders.BrowserName,
    browserVersion:
      browser.version || LogMissingParamPlaceholders.BrowserVersion,
    osName: os.name || LogMissingParamPlaceholders.OsName,
    osVersion: os.version || LogMissingParamPlaceholders.OsVersion,
    deviceModel: device.model || LogMissingParamPlaceholders.Device,
    deviceVendor: device.vendor || LogMissingParamPlaceholders.DeviceVendor,
    deviceType: device.type || LogMissingParamPlaceholders.DeviceType
  }
}

export const log = (
  { message, appName, access_token, id_token, otherParams }: LogRawData,
  logMethod: LogMethods = 'debug'
): Promise<void> => {
  const logData: LogFormattedData = {
    isError: false,
    appName,
    message: makeMessage(message, window, access_token, id_token, otherParams)
  }

  const result = logger[logMethod](logData) as unknown

  return result ? (result as Promise<void>) : Promise.resolve()
}

export const logError = ({
  message,
  appName,
  access_token,
  id_token,
  otherParams
}: LogRawData): Promise<void> => {
  const errorData: LogFormattedData = {
    isError: true,
    appName,
    message: makeMessage(message, window, access_token, id_token, otherParams)
  }

  const result = logger.error(errorData) as unknown

  return result ? (result as Promise<void>) : Promise.resolve()
}

export default logger
